import { Upload, message } from "antd";

/**
 * Tiện ích upload ảnh dùng chung cho các form CMS (banner, article, rank...).
 * - beforeUpload: chặn auto-upload, validate là ảnh + < 2MB.
 * - compressImage: resize + nén JPEG phía trình duyệt trước khi gửi (giảm dung lượng, upload nhanh).
 * - getBase64: đọc file -> dataURL để preview.
 */

const DEFAULT_MAX_MB = 2;

/**
 * Dùng cho AntD Upload beforeUpload: validate rồi trả false để giữ file client-side
 * (không tự upload lên server), file mới nằm ở fileList[i].originFileObj.
 */
export const beforeUpload = (file, { maxMB = DEFAULT_MAX_MB } = {}) => {
  const isImage = file.type?.startsWith("image/");
  if (!isImage) {
    message.error("Bạn chỉ có thể tải lên file hình ảnh!");
    return Upload.LIST_IGNORE;
  }
  const isLtMax = file.size / 1024 / 1024 < maxMB;
  if (!isLtMax) {
    message.error(`Hình ảnh phải nhỏ hơn ${maxMB}MB!`);
    return Upload.LIST_IGNORE;
  }
  return false;
};

/** Đọc file -> base64 dataURL để preview. */
export const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

/**
 * Nén ảnh: resize cạnh dài về tối đa maxSize, encode JPEG quality.
 * Trả về File (giữ tên, đuôi .jpg). Fallback trả file gốc nếu trình duyệt không hỗ trợ
 * hoặc bản nén không nhỏ hơn.
 */
export const compressImage = async (
  file,
  { maxSize = 1600, quality = 0.8 } = {}
) => {
  try {
    if (!file || !file.type?.startsWith("image/")) return file;
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (width > maxSize || height > maxSize) {
      const scale = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    );
    if (blob && blob.size < file.size) {
      const name = (file.name || "image").replace(/\.\w+$/, "") + ".jpg";
      return new File([blob], name, { type: "image/jpeg" });
    }
    return file;
  } catch {
    return file; // không hỗ trợ canvas/bitmap -> gửi ảnh gốc
  }
};
